import { ArrowLeft, Heart, Pencil, Share2, Trash2 } from "lucide-react"
import { useCallback } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface PageHeaderProps {
  title?: string
  showBackButton?: boolean
  showEdit?: boolean
  showDelete?: boolean
  showShare?: boolean
  showFavorite?: boolean
  isFavorite?: boolean
  favoritesCount?: number
  editHref?: string
  onBack?: () => void
  onDelete?: () => void
  onShare?: () => void
  onFavorite?: (isFavorite: boolean) => void
  className?: string
  children?: React.ReactNode
}

export function PageHeader({
  title,
  showBackButton = false,
  showEdit = false,
  showDelete = false,
  showShare = false,
  showFavorite = false,
  isFavorite = false,
  favoritesCount,
  editHref = "#",
  onBack,
  onDelete,
  onShare,
  onFavorite,
  className,
  children,
}: PageHeaderProps) {
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
      return
    }
    window.history.back()
  }, [onBack])

  const defaultOnShare = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({
          title: document.title,
          url: window.location.href,
        })
        .catch(console.error)
    } else {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          console.log("URL copiada al portapapeles")
        })
        .catch(console.error)
    }
  }, [])

  const handleShare = onShare ?? defaultOnShare

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {/* Back button (left) */}
      {showBackButton && (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={handleBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
      )}

      {/* Title (center/left grow) */}
      {title && (
        <h1 className="text-foreground truncate text-base font-semibold">
          {title}
        </h1>
      )}

      {/* Actions (right) */}
      {(showEdit || showDelete || showShare || showFavorite || children) && (
        <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
          {children}

          {showShare && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="text-foreground"
            >
              <Share2 className="h-4 w-4" />
              Compartir
            </Button>
          )}

          {showFavorite && onFavorite && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onFavorite(!isFavorite)}
              className={cn("text-foreground", {
                "text-destructive": isFavorite,
              })}
            >
              <Heart
                className={cn("h-4 w-4", {
                  "fill-current": isFavorite,
                })}
              />
              {favoritesCount !== undefined && favoritesCount > 0 && (
                <span className="ml-1 text-xs">{favoritesCount}</span>
              )}
              <span className="sr-only">
                {isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
              </span>
            </Button>
          )}

          {showEdit && (
            <Button variant="outline" size="sm" asChild>
              <a href={editHref}>
                <Pencil className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Editar</span>
              </a>
            </Button>
          )}

          {showDelete && onDelete && (
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Eliminar</span>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
